/**
 * Zod schemas mirroring the OpenMarkets Flow API, per docs/API-NOTES.md and live
 * responses. Objects use `.passthrough()` so undocumented/extra fields don't cause
 * validation failures (the API is young and drifts — the point of greenbook is to
 * catch that drift, not choke on it). Documented fields are typed; genuinely
 * unknown shapes are `z.unknown()`. `z.infer` types are exported alongside.
 */
import { z } from "zod";
/* ------------------------------------------------------------------ envelope */
export const paginationSchema = z
    .object({
    next_cursor: z.string().nullable(),
    has_more: z.boolean(),
    limit: z.number(),
})
    .passthrough();
export const metaSchema = z
    .object({
    timestamp: z.string(),
    api_version: z.string(),
})
    .passthrough();
/** Build a full success-envelope schema around a `data` payload schema. */
export function envelopeSchema(data) {
    return z
        .object({
        data,
        pagination: paginationSchema.optional(),
        meta: metaSchema.optional(),
    })
        .passthrough();
}
export const errorEnvelopeSchema = z
    .object({
    error: z
        .object({
        code: z.string(),
        message: z.string(),
        details: z.record(z.string(), z.unknown()).optional(),
    })
        .passthrough(),
})
    .passthrough();
/* ------------------------------------------------------------------- catalog */
export const leagueSchema = z
    .object({
    id: z.string(),
    name: z.string(),
    // Verified live 2026-07-09: nullable (some leagues report `null`), and values
    // include "athlete" beyond the documented "team". Kept as a nullable string.
    participant_type: z.string().nullable().optional(),
    status: z.string().optional(),
    // Undocumented but present on every league.
    image_url: z.string().nullable().optional(),
})
    .passthrough();
export const participantSchema = z
    .object({
    id: z.string(),
    league_id: z.string().optional(),
    name: z.string().optional(),
})
    .passthrough();
export const contestSchema = z
    .object({
    id: z.string(),
    league_id: z.string().optional(),
    title: z.string().optional(),
    // Observed live: "match" (head-to-head game), "tournament" (futures/outright),
    // "team" (per-team outright markets).
    type: z.string().optional(),
    starts_at: z.string().optional(),
    status: z.string().optional(),
    // Undocumented but present on every contest — whether it has any tradeable markets.
    // Drives the "tradeable only" filter so dead-end contests don't pad the list.
    has_markets: z.boolean().optional(),
    // For `match` contests, tags carry `home_team`/`away_team` objects (abbr/name/country)
    // — the matchup inline, no participants join needed.
    tags: z.record(z.string(), z.unknown()).optional(),
    created_at: z.string().optional(),
    updated_at: z.string().optional(),
})
    .passthrough();
/**
 * Market definition from `GET /contests/:id/markets`. Carries the layout-critical
 * shape flags (`variable_required`, `allow_multiple_participants`) that liquidity
 * positions do NOT — join `position.market_key ↔ market.key`. Semantics confirmed
 * by OpenMarkets 2026-07-14 (see docs/API-NOTES.md "Market-shape model").
 */
export const marketSchema = z
    .object({
    id: z.string().optional(),
    key: z.string().optional(),
    market_key: z.string().optional(),
    name: z.string().optional(),
    label: z.string().optional(),
    title: z.string().optional(),
    description: z.string().nullable().optional(),
    category: z.string().nullable().optional(),
    type: z.string().optional(),
    // Layout-critical flags.
    variable_required: z.boolean().optional(),
    allow_multiple_participants: z.boolean().optional(),
    default_variable_type: z.string().nullable().optional(),
    participant_type: z.string().nullable().optional(),
    status: z.string().optional(),
})
    .passthrough();
/* ---------------------------------------------------------- liquidity & depth */
export const partnerLiquiditySchema = z
    .object({
    partner_id: z.string(),
    partner_name: z.string().optional(),
    price: z.number().nullable().optional(),
    american_odds: z.string().nullable().optional(),
    available: z.number().nullable().optional(),
    // Verified live 2026-07-09 (first contest observed WITH liquidity): partner
    // entries also carry these undocumented fields. Kept optional; passthrough
    // preserves anything else. See FRICTION.md.
    liquidity_hash: z.string().optional(),
    buy_id: z.string().nullable().optional(),
    edges: z.record(z.string(), z.unknown()).optional(),
    fee_packet: z
        .object({
        taker_price: z.number().nullable().optional(),
        maker_price: z.number().nullable().optional(),
        taker_fee: z.number().nullable().optional(),
        maker_fee: z.number().nullable().optional(),
    })
        .passthrough()
        .optional(),
})
    .passthrough();
export const liquidityPositionSchema = z
    .object({
    position_hash: z.string(),
    contest_id: z.string().optional(),
    title: z.string().optional(),
    market_key: z.string().optional(),
    // Verified live 2026-07-09: undocumented but present on every position — the
    // human market/participant labels and the line variable the UI needs to
    // group and label rows without extra lookups.
    market_label: z.string().optional(),
    variable: z.string().nullable().optional(),
    side_key: z.string().optional(),
    participant_id: z.string().nullable().optional(),
    participant_name: z.string().nullable().optional(),
    participant_image_url: z.string().nullable().optional(),
    consensus_line: z.string().nullable().optional(),
    consensus_price: z.number().nullable().optional(),
    distribution_prices: z.array(z.unknown()).optional(),
    paper_bettable: z.boolean().optional(),
    partner_liquidities: z.array(partnerLiquiditySchema).optional().default([]),
})
    .passthrough();
export const depthLevelSchema = z
    .object({
    price: z.number(),
    available: z.number().nullable().optional(),
})
    .passthrough();
export const depthPartnerSchema = z
    .object({
    partner_id: z.string(),
    partner_name: z.string().optional(),
    levels: z.array(depthLevelSchema).optional().default([]),
    total_available: z.number().nullable().optional(),
    best_price: z.number().nullable().optional(),
    fetched_at: z.string().optional(),
})
    .passthrough();
export const depthSchema = z
    .object({
    position_hash: z.string(),
    fetched_at: z.string().optional(),
    partners: z.array(depthPartnerSchema).optional().default([]),
})
    .passthrough();
/* ------------------------------------------------------------- contest state */
export const clockSchema = z
    .object({
    display: z.string().nullable().optional(),
    remaining_seconds: z.number().nullable().optional(),
    is_running: z.boolean().optional(),
})
    .passthrough();
export const periodSchema = z
    .object({
    number: z.number().nullable().optional(),
    label: z.string().nullable().optional(),
    type: z.string().nullable().optional(),
})
    .passthrough();
export const scoresSchema = z
    .object({
    // Docs say `{home, away}`; live (2026-07-09) it's `{home_score, away_score,
    // period_scores}` — both are accepted so callers can read whichever exists.
    // See FRICTION.md.
    home: z.number().nullable().optional(),
    away: z.number().nullable().optional(),
    home_score: z.number().nullable().optional(),
    away_score: z.number().nullable().optional(),
    period_scores: z.array(z.unknown()).optional(),
})
    .passthrough();
export const gameStateSchema = z
    .object({
    game_status: z.string().optional(),
    // Verified live 2026-07-09: pregame states send `clock: null` / `period: null`
    // (not just absent), so these must be nullable, not merely optional.
    clock: clockSchema.nullable().optional(),
    period: periodSchema.nullable().optional(),
    contest_result: z.unknown().optional(),
    winner_participant_id: z.string().nullable().optional(),
    scores: scoresSchema.nullable().optional(),
    situation: z.unknown().optional(),
})
    .passthrough();
export const contestStateSchema = z
    .object({
    contest_id: z.string(),
    state: gameStateSchema.nullable().optional(),
    // Undocumented top-level flag observed live.
    live: z.boolean().optional(),
    // Verified live 2026-07-09: null in pregame, not absent.
    refreshed_at: z.string().nullable().optional(),
    cached_at: z.string().nullable().optional(),
})
    .passthrough();
/** `POST /contest-state/batch` → `data` is a map keyed by contest_id. */
export const contestStateBatchSchema = z.record(z.string(), contestStateSchema);
/* ------------------------------------------------------------------ partners */
export const credentialFieldSchema = z
    .object({
    key: z.string(),
    label: z.string().optional(),
    type: z.string().optional(),
    required: z.boolean().optional(),
})
    .passthrough();
/** Per-venue order rules from `GET /partners.order_config` (native price units). */
export const orderConfigSchema = z
    .object({
    orders_allowed: z.boolean().nullable().optional(),
    credentials_allowed: z.boolean().nullable().optional(),
    min_order_amount: z.number().nullable().optional(),
    max_order_amount: z.number().nullable().optional(),
    amount_increment: z.number().nullable().optional(),
    /** true = stake must buy a whole number of contracts (round down; can be `unfillable`). */
    whole_contracts: z.boolean().nullable().optional(),
    full_fill_always_allowed: z.boolean().nullable().optional(),
    /** true = venue expects an explicit price (a `max_price` outside the band → `price_out_of_band`). */
    price_submitted: z.boolean().nullable().optional(),
    /** "american" | "cents" | "decimal_probability" — unit for the price_* fields below. */
    price_format: z.string().nullable().optional(),
    price_tick: z.number().nullable().optional(),
    min_price: z.number().nullable().optional(),
    max_price: z.number().nullable().optional(),
    cancel_supported: z.boolean().nullable().optional(),
    /** Which order types this venue accepts (live 2026-07-24), e.g. ["market","limit"]
     *  or ["market"]. Gate the ticket's market/limit toggle on this. */
    supported_order_types: z.array(z.string()).nullable().optional(),
})
    .passthrough();
export const partnerSchema = z
    .object({
    partner_id: z.string(),
    name: z.string().optional(),
    status: z.string().optional(),
    // `logo_url` now present on the `/partners` catalog (verified 2026-07-14) — the
    // account-side endpoints still lack it (FRICTION.md), so this is the join source.
    logo_url: z.string().nullable().optional(),
    // Documented on the "Venues & balances" page (2026-07-14) as a bool; verified
    // live on all 11 partners (8 true; AlphaArcade/4Casters/SXBet false).
    orders_allowed: z.boolean().nullable().optional(),
    // Undocumented but present on `/partners` (2026-07-14): whether a customer can
    // connect the venue, whether it's an internal book, and per-venue order config.
    connectable: z.boolean().nullable().optional(),
    internal_book: z.boolean().nullable().optional(),
    // Per-venue order rules (live). Join by `partner_id` to a `partner_liquidities[]`
    // cell to pre-size the ticket (min/max, whole-contract, price band) — the server
    // enforces the same rules and returns `rejected_code`, so this is an optimization.
    // NOTE: `min_price`/`max_price`/`price_tick` are in NATIVE units per `price_format`
    // (e.g. Kalshi `cents` 1–99, Polymarket `decimal_probability` 0.001–0.999), NOT the
    // 0–1 probability the liquidity feed quotes.
    order_config: orderConfigSchema.optional(),
    credential_fields: z.array(credentialFieldSchema).optional().default([]),
})
    .passthrough();
/* -------------------------------------------------------- auth introspection */
export const currentKeySchema = z
    .object({
    api_key_id: z.string().optional(),
    name: z.string().nullable().optional(),
    key_prefix: z.string().optional(),
    key_type: z.string().optional(),
    rate_limit_per_minute: z.number().nullable().optional(),
    rate_limit_per_day: z.number().nullable().optional(),
    last_used_at: z.string().nullable().optional(),
})
    .passthrough();
export const usageSchema = z
    .object({
    requests_last_24h: z.number().nullable().optional(),
    requests_last_7d: z.number().nullable().optional(),
    errors_last_24h: z.number().nullable().optional(),
})
    .passthrough();
export const creditsSchema = z
    .object({
    allowance: z.number().nullable().optional(),
    purchased: z.number().nullable().optional(),
    total: z.number().nullable().optional(),
    current_period: z.unknown().optional(),
    included_per_month: z.number().nullable().optional(),
    used_this_period: z.number().nullable().optional(),
})
    .passthrough();
export const authMeSchema = z
    .object({
    auth_method: z.string().optional(),
    player: z.unknown().nullable().optional(),
    organization_id: z.string().nullable().optional(),
    router_account_id: z.string().nullable().optional(),
    can_execute: z.boolean().optional(),
    current_key: currentKeySchema.optional(),
    usage: usageSchema.optional(),
    credits: creditsSchema.optional(),
})
    .passthrough();
/* --------------------------------------------- account & execution surface */
/*
 * Discovered 2026-07-09 (Connect guide + live probing). All verified against the
 * production account `pg-1761242872395-lqal6f7-1`. Documented fields are typed;
 * `.passthrough()` keeps the rest. NOTE: `account/partners` returns an
 * `encrypted_credentials` blob — typed here as returned, but the greenbook proxy
 * STRIPS it before it reaches any browser (see apps/web/server/proxy.ts + test).
 */
/** One connected venue from `GET /auth/account/partners`. */
export const accountPartnerSchema = z
    .object({
    account_partner_id: z.string().optional(),
    router_account_id: z.string().optional(),
    partner_id: z.string(),
    partner_name: z.string().nullable().optional(),
    status: z.string().optional(),
    credential_status: z.string().optional(),
    // Credential ciphertext — present in the raw API response. Typed so the schema
    // matches reality; the server proxy deletes it. Never render this.
    encrypted_credentials: z.string().nullable().optional(),
    balance: z.number().nullable().optional(),
    // Flow balance-streaming update (observed 2026-08-24): prefer `expected_balance` for
    // display. `balance` is only re-read from the venue ~every 60s and can be stale;
    // `expected_balance` is adjusted live for orders you've created/cancelled since. The
    // companion metadata says how fresh `balance` is and how much is held by open orders.
    expected_balance: z.number().nullable().optional(),
    pending: z.number().nullable().optional(),
    is_stale: z.boolean().nullable().optional(),
    balance_as_of: z.string().nullable().optional(),
    balance_version: z.number().nullable().optional(),
    balance_source: z.string().nullable().optional(),
    currency: z.string().nullable().optional(),
    // Round-2 fix (PR #599, live 2026-07-15): venue branding now on the account row,
    // so a balances grid no longer has to harvest logos from orders/opportunities.
    logo_url: z.string().nullable().optional(),
    create_datetime: z.string().nullable().optional(),
    last_update_datetime: z.string().nullable().optional(),
})
    .passthrough();
/**
 * One entry of the Round-2 `currencies[]` array on `GET /auth/account/partners`
 * (PR #599, live 2026-07-15): the real/play (USD/ATLAS) split is now derivable from
 * this endpoint too, not only `/auth/account/balances`.
 */
export const accountCurrencySchema = z
    .object({
    currency: z.string(),
    is_real_money: z.boolean().optional(),
    symbol: z.string().nullable().optional(),
    precision: z.number().nullable().optional(),
    label: z.string().nullable().optional(),
    total: z.number().nullable().optional(),
})
    .passthrough();
export const accountPartnersSchema = z
    .object({
    router_account_id: z.string().optional(),
    total_balance: z.number().nullable().optional(),
    // Account-level live total that accounts for open-order holds (see `expected_balance`).
    expected_total_balance: z.number().nullable().optional(),
    balances_by_currency: z.record(z.string(), z.number()).optional(),
    currencies: z.array(accountCurrencySchema).optional().default([]),
    account_partners: z.array(accountPartnerSchema).optional().default([]),
})
    .passthrough();
/** One venue inside a currency bucket from `GET /auth/account/balances`. */
export const balancePartnerSchema = z
    .object({
    partner_id: z.string(),
    partner_name: z.string().nullable().optional(),
    balance: z.number().nullable().optional(),
    // Live, order-aware balance (see accountPartnerSchema.expected_balance) — present when
    // the balances endpoint carries it; consumers prefer it over `balance` for display.
    expected_balance: z.number().nullable().optional(),
    pending: z.number().nullable().optional(),
    // Surface staleness rather than hide it: is_stale means the snapshot is older than the
    // refresh SLA (or was never taken); balance_source om_wallet is exact/never stale, null
    // means the venue exposes no balance so no honest expected figure exists.
    is_stale: z.boolean().nullable().optional(),
    balance_as_of: z.string().nullable().optional(),
    balance_source: z.string().nullable().optional(),
    status: z.string().optional(),
    credential_status: z.string().optional(),
    internal_book: z.boolean().optional(),
})
    .passthrough();
export const currencyBalanceSchema = z
    .object({
    currency: z.string(),
    is_real_money: z.boolean().optional(),
    symbol: z.string().nullable().optional(),
    precision: z.number().nullable().optional(),
    label: z.string().nullable().optional(),
    total: z.number().nullable().optional(),
    partners: z.array(balancePartnerSchema).optional().default([]),
})
    .passthrough();
export const accountBalancesSchema = z
    .object({
    router_account_id: z.string().optional(),
    by_currency: z.array(currencyBalanceSchema).optional().default([]),
})
    .passthrough();
/** One raw venue event from `GET /auth/account/history`. */
export const historyRecordSchema = z
    .object({
    partner_id: z.string().optional(),
    external_ref: z.string().nullable().optional(),
    external_market_ref: z.string().nullable().optional(),
    kind: z.string().optional(), // "fill" | "transaction" | …
    side: z.string().nullable().optional(),
    instrument_label: z.string().nullable().optional(),
    quantity: z.number().nullable().optional(),
    price: z.number().nullable().optional(),
    status: z.string().nullable().optional(),
    cost: z.number().nullable().optional(),
    payout: z.number().nullable().optional(),
    outcome: z.string().nullable().optional(),
    position_key: z.string().nullable().optional(),
    settled_pnl: z.number().nullable().optional(),
    occurred_at: z.string().nullable().optional(),
    router_contest_id: z.string().nullable().optional(),
    contest_label: z.string().nullable().optional(),
    router_order_id: z.string().nullable().optional(),
    placed_via_openmarkets: z.boolean().optional(),
})
    .passthrough();
export const accountHistorySchema = z
    .object({
    om_account_id: z.string().optional(),
    records: z.array(historyRecordSchema).optional().default([]),
})
    .passthrough();
/**
 * `POST /auth/account/history/sync` (Round-2, PR #603, live 2026-07-15) — dispatches
 * a fire-and-forget per-venue re-sync of the raw venue ledger. Returns immediately;
 * the history/positions stores update async over the next few seconds.
 */
export const historySyncSchema = z
    .object({
    om_account_id: z.string().optional(),
    dispatched_partners: z.array(z.string()).optional().default([]),
})
    .passthrough();
/** Funding descriptor carried on orders/opportunities. */
export const fundingSchema = z
    .object({
    currency: z.string().optional(),
    is_real_money: z.boolean().optional(),
    symbol: z.string().nullable().optional(),
    precision: z.number().nullable().optional(),
    label: z.string().nullable().optional(),
})
    .passthrough();
/* ---------------------------------------------------------------- performance */
/*
 * `GET /flow/v1/auth/performance` (Round-2, PR #601 docs, live 2026-07-15) — the
 * OM-native, fresh P&L analytics endpoint. Params: `group_by` =
 * none|day|league|partner|model|opportunity_type, `from`/`to` = ISO YYYY-MM-DD.
 * Currency-partitioned: USD is real money, ATLAS is play. `group_by=none` yields
 * portfolio totals (empty `groups`). Group `key`s are NOT date-sorted — index by key.
 */
export const performanceGroupSchema = z
    .object({
    key: z.string(),
    label: z.string().nullable().optional(),
    staked: z.number().nullable().optional(),
    net_pl: z.number().nullable().optional(),
    roi: z.number().nullable().optional(),
    settled_count: z.number().nullable().optional(),
    wins: z.number().nullable().optional(),
    losses: z.number().nullable().optional(),
    pushes: z.number().nullable().optional(),
})
    .passthrough();
export const performanceCurrencySchema = z
    .object({
    funding: fundingSchema.optional(),
    staked: z.number().nullable().optional(),
    net_pl: z.number().nullable().optional(),
    roi: z.number().nullable().optional(),
    settled_count: z.number().nullable().optional(),
    pending_count: z.number().nullable().optional(),
    wins: z.number().nullable().optional(),
    losses: z.number().nullable().optional(),
    pushes: z.number().nullable().optional(),
    cancelled: z.number().nullable().optional(),
    groups: z.array(performanceGroupSchema).optional().default([]),
})
    .passthrough();
export const performanceSchema = z
    .object({
    by_currency: z.array(performanceCurrencySchema).optional().default([]),
    from: z.string().nullable().optional(),
    to: z.string().nullable().optional(),
    group_by: z.string().nullable().optional(),
})
    .passthrough();
/** Partner descriptor with a logo — the only place the API hands out `logo_url`. */
export const orderPartnerSchema = z
    .object({
    partner_id: z.string().optional(),
    partner_name: z.string().nullable().optional(),
    logo_url: z.string().nullable().optional(),
    internal_book: z.boolean().optional(),
})
    .passthrough();
/** One order/leg from `GET /auth/orders` and inside opportunity `legs[]`. */
export const orderSchema = z
    .object({
    router_order_id: z.string(),
    partner_id: z.string().optional(),
    currency: z.string().nullable().optional(),
    funding: fundingSchema.optional(),
    partner: orderPartnerSchema.optional(),
    contest_id: z.string().nullable().optional(),
    market_id: z.string().nullable().optional(),
    market_side_id: z.string().nullable().optional(),
    title: z.string().nullable().optional(),
    side: z.string().nullable().optional(),
    amount: z.number().nullable().optional(),
    price: z.number().nullable().optional(),
    amount_with_fee: z.number().nullable().optional(),
    fee: z.number().nullable().optional(),
    potential_payout: z.number().nullable().optional(),
    status: z.string().nullable().optional(),
    order_type: z.string().nullable().optional(),
    fill_status: z.string().nullable().optional(),
    filled_amt: z.number().nullable().optional(),
    unfilled_amt: z.number().nullable().optional(),
    partner_order_status: z.string().nullable().optional(),
    opportunity_type: z.string().nullable().optional(),
    opportunity_id: z.string().nullable().optional(),
    /** The model the order was filed under (null = none). Docs: Models & attribution. */
    model_id: z.string().nullable().optional(),
    model_name: z.string().nullable().optional(),
    leg_number: z.number().nullable().optional(),
    liquidity_hash: z.string().nullable().optional(),
    // Settlement block (per the router_order shape in the execution docs).
    result: z.string().nullable().optional(),
    roi: z.number().nullable().optional(),
    winnings: z.number().nullable().optional(),
    net_winnings: z.number().nullable().optional(),
    failure_reason: z.string().nullable().optional(),
    placed_datetime: z.string().nullable().optional(),
    settled_datetime: z.string().nullable().optional(),
    last_update_datetime: z.string().nullable().optional(),
    created_at: z.string().nullable().optional(),
})
    .passthrough();
/** One play from `GET /auth/opportunities`. */
export const opportunitySchema = z
    .object({
    opportunity_id: z.string(),
    opportunity_type: z.string().optional(),
    funding: fundingSchema.optional(),
    contest_id: z.string().nullable().optional(),
    model_id: z.string().nullable().optional(),
    model_name: z.string().nullable().optional(),
    status: z.string().optional(),
    fill_status: z.string().nullable().optional(),
    expected_profit: z.number().nullable().optional(),
    expected_roi: z.number().nullable().optional(),
    total_bet_amount: z.number().nullable().optional(),
    total_winnings: z.number().nullable().optional(),
    total_net_winnings: z.number().nullable().optional(),
    total_fees: z.number().nullable().optional(),
    actual_profit: z.number().nullable().optional(),
    actual_roi: z.number().nullable().optional(),
    partners: z.array(orderPartnerSchema).optional().default([]),
    legs: z.array(orderSchema).optional().default([]),
})
    .passthrough();
/**
 * One model from `/auth/models` (docs: Models & attribution). A label orders are filed under;
 * `status` is `active` | `archived`. Observed live 2026-10-29: the list carries no pagination.
 */
export const modelSchema = z
    .object({
    model_id: z.string(),
    model_name: z.string(),
    model_description: z.string().nullable().optional(),
    league_id: z.string().nullable().optional(),
    league_name: z.string().nullable().optional(),
    status: z.string(),
    created_at: z.string().nullable().optional(),
    updated_at: z.string().nullable().optional(),
})
    .passthrough();
/** One rolled-up position from the MCP `list_account_positions` tool. */
export const accountPositionSchema = z
    .object({
    partner_id: z.string().optional(),
    position_key: z.string().nullable().optional(),
    external_market_ref: z.string().nullable().optional(),
    instrument_label: z.string().nullable().optional(),
    side: z.string().nullable().optional(),
    contracts: z.number().nullable().optional(),
    cost_basis: z.number().nullable().optional(),
    payout: z.number().nullable().optional(),
    net_pnl: z.number().nullable().optional(),
    outcome: z.string().nullable().optional(),
    status: z.string().optional(),
    opened_at: z.string().nullable().optional(),
    settled_at: z.string().nullable().optional(),
    event_count: z.number().nullable().optional(),
    partner_market_id: z.string().nullable().optional(),
    router_contest_id: z.string().nullable().optional(),
    contest_label: z.string().nullable().optional(),
    placed_via_openmarkets: z.boolean().optional(),
})
    .passthrough();
export const accountPositionsSchema = z
    .object({
    om_account_id: z.string().optional(),
    positions: z.array(accountPositionSchema).optional().default([]),
})
    .passthrough();
/** One point from `GET /contests/:id/price-history` (epoch-ms timestamp). */
export const pricePointSchema = z
    .object({
    timestamp: z.number(),
    position_hash: z.string().optional(),
    partner_id: z.string().optional(),
    partner_name: z.string().nullable().optional(),
    price: z.number().nullable().optional(),
    available: z.number().nullable().optional(),
})
    .passthrough();
export const priceHistorySchema = z
    .object({
    contest_id: z.string().optional(),
    resolution: z.string().optional(),
    from: z.number().nullable().optional(),
    to: z.number().nullable().optional(),
    points: z.array(pricePointSchema).optional().default([]),
})
    .passthrough();
/* ----------------------------------------------------- execution (M2) */
/*
 * `POST /auth/orders/buy` and friends. Execution is VENUE-DIRECTED today (SOR
 * "coming soon"): every leg names its venue via `liquidity_hash` (already
 * venue-scoped) OR `position_hash` + explicit `partner_id`. Bodies are strict —
 * the API rejects unknown fields with 400 `unknown_parameter`. See
 * docs/API-NOTES.md "Execution docs".
 */
export const orderModeSchema = z.enum(["real", "paper"]);
/**
 * Flow order-type vocabulary (live 2026-07-24). `market` (default) = immediate IOC —
 * fills what's available up to `max_price`, cancels the rest, never rests. `limit` =
 * posts a resting order at `max_price` until it fills or is cancelled. `limit` is only
 * accepted on venues whose `order_config.supported_order_types` includes it — otherwise
 * both `/buy` and `/preflight` return `400 unsupported_order_type`. (The internal name
 * `fill_or_kill` is not exposed.)
 */
export const orderTypeSchema = z.enum(["market", "limit"]);
/**
 * One leg of a buy request. `.strict()` mirrors the API's strict-body contract so
 * a stray field is caught client-side before we spend a request. `.superRefine`
 * enforces the venue-directed + stake XOR rules:
 *   - exactly one of `liquidity_hash` | `position_hash`
 *   - `partner_id` required with a bare `position_hash`
 *   - exactly one of `amount` (USD stake) | `shares`, each > 0
 *   - `max_price` strictly within (0, 1)
 */
export const buyOrderLegSchema = z
    .object({
    liquidity_hash: z.string().min(1).optional(),
    position_hash: z.string().min(1).optional(),
    partner_id: z.string().min(1).optional(),
    amount: z.number().positive().optional(),
    shares: z.number().positive().optional(),
    max_price: z.number().gt(0).lt(1),
    mode: orderModeSchema.optional(),
    // Optional; defaults to "market" server-side (today's IOC behavior). "limit" only
    // on venues whose order_config.supported_order_types includes it (else 400).
    order_type: orderTypeSchema.optional(),
})
    .strict()
    .superRefine((leg, ctx) => {
    const hasLiq = leg.liquidity_hash !== undefined;
    const hasPos = leg.position_hash !== undefined;
    if (hasLiq === hasPos) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Provide exactly one of liquidity_hash or position_hash",
            path: ["liquidity_hash"],
        });
    }
    if (hasPos && leg.partner_id === undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "partner_id is required when using position_hash",
            path: ["partner_id"],
        });
    }
    const hasAmt = leg.amount !== undefined;
    const hasShares = leg.shares !== undefined;
    if (hasAmt === hasShares) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Provide exactly one of amount or shares",
            path: ["amount"],
        });
    }
});
/** Multi-leg placement controls (live 2026-07-28); all default to today's behavior. */
export const onLegFailureSchema = z.enum(["continue", "halt"]);
export const placementSchema = z.enum(["sequential", "parallel"]);
export const buyRequestSchema = z.object({
    orders: z.array(buyOrderLegSchema).min(1),
    /** On a failed leg: `continue` (default) keeps placing the rest; `halt` stops. */
    on_leg_failure: onLegFailureSchema.optional(),
    /** `true` = validate/reserve every leg before placing any (all-or-nothing pre-check). */
    check_all_first: z.boolean().optional(),
    /** `sequential` (default) or `parallel` leg placement. */
    placement: placementSchema.optional(),
    /**
     * `true` (top-level, live 2026-07-28) = before routing, confirm each named venue's LIVE
     * orderbook when its cached liquidity is stale/thin. A leg whose book moved away comes
     * back `status:"rejected"`, `rejected_code:"insufficient_live_depth"` — declined before
     * it fires rather than placed-and-failing. Fresh/deep venues skip the extra check.
     */
    verify_liquidity: z.boolean().optional(),
});
/** Per-venue outcome inside a buy result leg. */
export const buyVenueResultSchema = z
    .object({
    partner_id: z.string().optional(),
    success: z.boolean().optional(),
    router_order_id: z.string().nullable().optional(),
    error: z.string().nullable().optional(),
})
    .passthrough();
/** One leg's result from `POST /auth/orders/buy`, keyed by request `index`. */
export const buyResultSchema = z
    .object({
    index: z.number().nullable().optional(),
    // "completed" (filled) | "failed" (placed/blocked, no fill — see rejected_code) |
    // "rejected" (router refused pre-dispatch) | "resting" (non-terminal; a limit order
    // sitting on the book — track via /orders/resting + the orders WS channel). Live
    // 2026-07-24 (#667).
    status: z.string().nullable().optional(),
    sor_decision_id: z.string().nullable().optional(),
    filled_notional: z.number().nullable().optional(),
    filled_shares: z.number().nullable().optional(),
    // The part of the stake that did NOT fill (live 2026-07-24, #664). On whole-contract
    // venues a market order fills whole contracts and the fractional remainder is left
    // here; `filled_notional` is now the ACTUAL booked fill, not the requested stake.
    unfilled_notional: z.number().nullable().optional(),
    avg_price: z.number().nullable().optional(),
    rejected_reason: z.string().nullable().optional(),
    // Machine-readable reject code (live 2026-07-23). Present on `status:"failed"`
    // legs; branch on THIS, not HTTP status (a blocked leg is a 200, not a 403).
    // Known values: venue_disabled | execution_limit_exceeded | orders_not_supported
    // | below_min_order | above_max_order | unfillable | price_out_of_band | halted_upstream
    // | insufficient_balance | insufficient_live_depth (from verify_liquidity). Kept a loose
    // string (not an enum) so a new server code never fails validation.
    rejected_code: z.string().nullable().optional(),
    // Round-2 (PR #602, live 2026-07-15): echoes the venue the caller named, so a
    // paper→internal-book substitution is detectable by comparing this to the
    // filled `venues[].partner_id`. See FRICTION.md (paper-order routing).
    requested_partner_id: z.string().nullable().optional(),
    venues: z.array(buyVenueResultSchema).optional().default([]),
})
    .passthrough();
export const buyResponseSchema = z
    .object({
    results: z.array(buyResultSchema).optional().default([]),
    /** With `on_leg_failure:"halt"`, the leg index where placement stopped (live 2026-07-28). */
    halted_at_index: z.number().nullable().optional(),
})
    .passthrough();
/* ------------------------------------------------------------------ arbitrage */
// GET /arbitrages + the `arbitrage` WS channel (`arbitrage.update`), live 2026-07-28,
// needs `arbitrage:read`. combined_price < 1 ⇒ buying every side guarantees $1 for
// `combined_price` (risk-free). Each side lists the best option per venue.
export const arbitrageOptionSchema = z
    .object({
    partner_id: z.string().nullable().optional(),
    partner_name: z.string().nullable().optional(),
    price: z.number().nullable().optional(),
    after_fee_price: z.number().nullable().optional(),
    available: z.number().nullable().optional(),
    title: z.string().nullable().optional(),
    position_hash: z.string().nullable().optional(),
    source: z.string().nullable().optional(),
})
    .passthrough();
export const arbitrageSideSchema = z
    .object({
    market_side_id: z.string().nullable().optional(),
    title: z.string().nullable().optional(),
    options: z.array(arbitrageOptionSchema).optional().default([]),
})
    .passthrough();
export const arbitrageSchema = z
    .object({
    arbitrage_id: z.string(),
    contest: z
        .object({
        id: z.string().nullable().optional(),
        label: z.string().nullable().optional(),
        league_id: z.string().nullable().optional(),
    })
        .passthrough()
        .optional(),
    market: z
        .object({
        id: z.string().nullable().optional(),
        variable_id: z.string().nullable().optional(),
    })
        .passthrough()
        .optional(),
    combined_price: z.number().nullable().optional(),
    roi_pct: z.number().nullable().optional(),
    after_fee_roi_pct: z.number().nullable().optional(),
    profitable_after_fees: z.boolean().nullable().optional(),
    max_investment: z.number().nullable().optional(),
    detected_at: z.string().nullable().optional(),
    sides: z.array(arbitrageSideSchema).optional().default([]),
})
    .passthrough();
export const arbitragesSchema = z.array(arbitrageSchema);
/**
 * `GET /flow/v1/signals` — a curated, explained feed of actionable signals (currently all
 * `signal_type:"arbitrage"`, but the shape is type-agnostic). Each carries a natural-language
 * `headline` + `explanation`, a `strength` (0–100) and `confidence`, the `position` it's on,
 * and a short `expires_at` (~2 min). Live pushes come on the `signal` WS channel
 * (`signal.update`, per contest — an empty `signals` array clears that contest). Observed
 * 2026-08-24 against the live API (docs auth-gated — see FRICTION.md).
 */
export const signalPositionSchema = z
    .object({
    position_hash: z.string().nullable().optional(),
    router_contest_id: z.string().nullable().optional(),
    router_league_id: z.string().nullable().optional(),
    router_market_id: z.string().nullable().optional(),
    market_key: z.string().nullable().optional(),
    participant_id: z.string().nullable().optional(),
    title: z.string().nullable().optional(),
    contest_label: z.string().nullable().optional(),
})
    .passthrough();
/** Signal `evidence` — arbitrage-shaped today; passthrough keeps future types intact. */
export const signalEvidenceSchema = z
    .object({
    arbitrage_id: z.string().nullable().optional(),
    after_fee_roi: z.number().nullable().optional(),
    best_combined_price: z.number().nullable().optional(),
    best_guaranteed_profit_pct: z.number().nullable().optional(),
    profitable_after_fees: z.boolean().nullable().optional(),
    best_max_investment: z.number().nullable().optional(),
    venues: z.array(z.string()).nullable().optional(),
})
    .passthrough();
export const signalSchema = z
    .object({
    signal_id: z.string(),
    dedupe_key: z.string().nullable().optional(),
    schema_version: z.number().nullable().optional(),
    status: z.string().nullable().optional(),
    signal_type: z.string().nullable().optional(),
    headline: z.string().nullable().optional(),
    explanation: z.string().nullable().optional(),
    strength: z.number().nullable().optional(),
    confidence: z.string().nullable().optional(),
    calibration_version: z.union([z.string(), z.number()]).nullable().optional(),
    detected_at: z.string().nullable().optional(),
    expires_at: z.string().nullable().optional(),
    position: signalPositionSchema.nullable().optional(),
    related_positions: z.array(signalPositionSchema).nullable().optional(),
    evidence: signalEvidenceSchema.nullable().optional(),
})
    .passthrough();
export const signalsSchema = z.array(signalSchema);
/** One working order from `GET /auth/orders/resting/me`. */
export const restingOrderSchema = z
    .object({
    router_order_id: z.string(),
    title: z.string().nullable().optional(),
    partner_id: z.string().nullable().optional(),
    partner_name: z.string().nullable().optional(),
    amount: z.number().nullable().optional(),
    price: z.number().nullable().optional(),
    fill_status: z.string().nullable().optional(), // "unfilled" | "partially_filled"
    filled_amt: z.number().nullable().optional(),
    unfilled_amt: z.number().nullable().optional(),
    placed_at: z.string().nullable().optional(),
    seconds_since_placed: z.number().nullable().optional(),
})
    .passthrough();
export const restingOrdersSchema = z
    .object({
    orders: z.array(restingOrderSchema).optional().default([]),
    count: z.number().nullable().optional(),
})
    .passthrough();
/**
 * `POST /auth/orders/cancel` response. LIVE shape (verified 2026-07-14) is
 * `{cancelled: [ids], skipped: [ids], errors: [{router_order_id, error}]}` — NOT
 * the `{router_order_ids, count}` the docs implied. Also: cancelling an owned but
 * un-cancellable order (already filled) returns **200 with a per-id `errors[]`
 * entry**, not the documented whole-batch `403 forbidden` (that path is for
 * ownership failures). See FRICTION.md. Kept permissive; passthrough keeps the rest.
 */
export const cancelErrorSchema = z
    .object({
    router_order_id: z.string().optional(),
    error: z.string().nullable().optional(),
})
    .passthrough();
export const cancelResponseSchema = z
    .object({
    cancelled: z.array(z.string()).optional().default([]),
    skipped: z.array(z.string()).optional().default([]),
    errors: z.array(cancelErrorSchema).optional().default([]),
    // Older/doc-implied fields kept optional for forward-compat.
    router_order_ids: z.array(z.string()).optional(),
    count: z.number().nullable().optional(),
})
    .passthrough();
/**
 * `GET /auth/orders/:id/resolution` — settlement detail. Docs describe "OM verdict
 * vs partner verdict + agreement + final score"; LIVE field names (verified
 * 2026-07-14, unsettled order) are `our_resolution` / `partner_resolution` /
 * `agreement` ("no_data" while unsettled) / `contest_final_state` / `result` — NOT
 * the doc names. See FRICTION.md. Kept permissive; passthrough retains anything else.
 */
export const orderResolutionSchema = z
    .object({
    router_order_id: z.string().optional(),
    result: z.string().nullable().optional(),
    agreement: z.string().nullable().optional(), // "no_data" while unsettled
    our_resolution: z.unknown().nullable().optional(),
    partner_resolution: z.unknown().nullable().optional(),
    contest_final_state: z.unknown().nullable().optional(),
})
    .passthrough();
/**
 * `POST /auth/account/partners/balance/update` — re-reads live balances from every
 * connected venue and returns the partners envelope plus `refreshed_partner_ids`.
 */
export const balanceRefreshSchema = accountPartnersSchema.extend({
    refreshed_partner_ids: z.array(z.string()).optional().default([]),
});
//# sourceMappingURL=index.js.map